import React, { useState, useEffect, useCallback } from 'react';
import {
  MapPin,
  Plus,
  MagnifyingGlass,
  PencilSimple,
  Trash,
  Buildings,
  X,
  Warning,
  ArrowsClockwise
} from '@phosphor-icons/react';
import { Header } from '../../components/layout';
import { Button, Modal } from '../../components/common';
import societyService from '../../services/societyService';
import './Locations.css';

export function Locations({ onMenuClick }) {
  const [activeTab, setActiveTab] = useState('pincodes');
  const [pincodes, setPincodes] = useState([]);
  const [societies, setSocieties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  // Modal states
  const [showPincodeModal, setShowPincodeModal] = useState(false);
  const [showSocietyModal, setShowSocietyModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [editingSociety, setEditingSociety] = useState(null);
  const [deletingSociety, setDeletingSociety] = useState(null);

  // Form states
  const [pincodeForm, setPincodeForm] = useState({ pincode: '' });
  const [societyForm, setSocietyForm] = useState({
    name: '',
    additionalAddress: '',
    pincode: ''
  });
  const [formErrors, setFormErrors] = useState({});
  const [saving, setSaving] = useState(false);

  // Fetch data
  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const [pincodesData, societiesData] = await Promise.all([
        societyService.getPincodes(),
        societyService.getSocieties()
      ]);
      setPincodes(pincodesData);
      setSocieties(societiesData);
    } catch (err) {
      console.error('Error fetching data:', err);
      setError('Failed to load data. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Get unique pincodes list
  const uniquePincodes = pincodes.map(p => p.pincode);

  // Filter based on active tab and search
  const filteredPincodes = pincodes.filter(p =>
    p.pincode.includes(searchTerm)
  );

  const filteredSocieties = societies.filter(s => {
    const searchLower = searchTerm.toLowerCase();
    return (
      s.name.toLowerCase().includes(searchLower) ||
      s.pincode.includes(searchTerm) ||
      (s.additionalAddress && s.additionalAddress.toLowerCase().includes(searchLower))
    );
  });

  // Stats
  const totalPincodes = pincodes.length;
  const totalSocieties = societies.length;

  // Handle Add Pincode
  const handleAddPincode = () => {
    setPincodeForm({ pincode: '' });
    setFormErrors({});
    setShowPincodeModal(true);
  };

  const handleSavePincode = async () => {
    const errors = {};
    if (!pincodeForm.pincode.trim()) {
      errors.pincode = 'Pincode is required';
    } else if (!/^\d{6}$/.test(pincodeForm.pincode)) {
      errors.pincode = 'Pincode must be 6 digits';
    } else if (uniquePincodes.includes(pincodeForm.pincode)) {
      errors.pincode = 'Pincode already exists';
    }

    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    // For pincodes, we create a placeholder society that can be updated later
    // Or we just add the pincode by creating a society
    try {
      setSaving(true);
      await societyService.createSociety({
        name: `New Society in ${pincodeForm.pincode}`,
        pincode: pincodeForm.pincode,
        additionalAddress: ''
      });
      setShowPincodeModal(false);
      fetchData();
    } catch (err) {
      console.error('Error adding pincode:', err);
      setFormErrors({ submit: err.response?.data?.message || 'Failed to add pincode' });
    } finally {
      setSaving(false);
    }
  };

  // Handle Add/Edit Society
  const handleAddSociety = () => {
    setEditingSociety(null);
    setSocietyForm({
      name: '',
      additionalAddress: '',
      pincode: uniquePincodes[0] || ''
    });
    setFormErrors({});
    setShowSocietyModal(true);
  };

  const handleEditSociety = (society) => {
    setEditingSociety(society);
    setSocietyForm({
      name: society.name,
      additionalAddress: society.additionalAddress || '',
      pincode: society.pincode
    });
    setFormErrors({});
    setShowSocietyModal(true);
  };

  const handleSaveSociety = async () => {
    const errors = {};
    if (!societyForm.name.trim()) {
      errors.name = 'Society name is required';
    }
    if (!societyForm.pincode) {
      errors.pincode = 'Please select a pincode';
    }

    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    try {
      setSaving(true);
      if (editingSociety) {
        await societyService.updateSociety(editingSociety._id, societyForm);
      } else {
        await societyService.createSociety(societyForm);
      }
      setShowSocietyModal(false);
      fetchData();
    } catch (err) {
      console.error('Error saving society:', err);
      setFormErrors({ submit: err.response?.data?.message || 'Failed to save society' });
    } finally {
      setSaving(false);
    }
  };

  // Handle Delete
  const handleDeleteClick = (society) => {
    setDeletingSociety(society);
    setShowDeleteModal(true);
  };

  const handleDeleteSociety = async () => {
    if (!deletingSociety) return;

    try {
      setSaving(true);
      await societyService.deleteSociety(deletingSociety._id);
      setShowDeleteModal(false);
      setDeletingSociety(null);
      fetchData();
    } catch (err) {
      console.error('Error deleting society:', err);
      setError('Failed to delete society');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <>
        <Header title="Locations" onMenuClick={onMenuClick} />
        <div className="page-content">
          <div className="loading-state">
            <ArrowsClockwise className="loading-spinner" size={32} />
            <p>Loading locations...</p>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Header title="Locations" onMenuClick={onMenuClick} />
      <div className="page-content">
        {/* Page Header */}
        <div className="locations-header">
          <div className="locations-header-content">
            <h2 className="section-title">Manage Locations</h2>
            <p className="section-subtitle">Service areas, pincodes and societies</p>
          </div>
          <div className="locations-header-actions">
            <button className="action-icon-btn" onClick={fetchData} title="Refresh">
              <ArrowsClockwise size={18} />
            </button>
            <Button variant="primary" onClick={activeTab === 'pincodes' ? handleAddPincode : handleAddSociety}>
              <Plus size={18} />
              Add {activeTab === 'pincodes' ? 'Pincode' : 'Society'}
            </Button>
          </div>
        </div>

        {/* Error Banner */}
        {error && (
          <div className="error-banner">
            <span>{error}</span>
            <button onClick={() => setError(null)}>
              <X size={18} />
            </button>
          </div>
        )}

        {/* Stats */}
        <div className="locations-stats">
        <div className="stat-card">
          <span className="stat-label">Total Pincodes</span>
          <span className="stat-value">{totalPincodes}</span>
        </div>
        <div className="stat-card">
          <span className="stat-label">Total Societies</span>
          <span className="stat-value stat-success">{totalSocieties}</span>
        </div>
      </div>

      {/* Tabs */}
      <div className="tabs-container">
        <div className="tabs-header">
          <button
            className={`tab-btn ${activeTab === 'pincodes' ? 'active' : ''}`}
            onClick={() => { setActiveTab('pincodes'); setSearchTerm(''); }}
          >
            <MapPin size={18} />
            Pincodes
            <span className="tab-count">{totalPincodes}</span>
          </button>
          <button
            className={`tab-btn ${activeTab === 'societies' ? 'active' : ''}`}
            onClick={() => { setActiveTab('societies'); setSearchTerm(''); }}
          >
            <Buildings size={18} />
            Societies
            <span className="tab-count">{totalSocieties}</span>
          </button>
        </div>

        {/* Tab Content */}
        <div className="tab-content">
          {/* Search */}
          <div className="tab-toolbar">
            <div className="search-wrapper">
              <MagnifyingGlass className="search-icon" size={18} />
              <input
                type="text"
                className="search-input"
                placeholder={activeTab === 'pincodes' ? 'Search pincodes...' : 'Search societies...'}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>

          {/* Pincodes Tab */}
          {activeTab === 'pincodes' && (
            <div className="table-container">
              {filteredPincodes.length === 0 ? (
                <div className="empty-state">
                  <MapPin size={48} className="empty-state-icon" />
                  <p>{searchTerm ? 'No pincodes found' : 'No pincodes added yet'}</p>
                  {!searchTerm && (
                    <Button variant="primary" onClick={handleAddPincode}>
                      <Plus size={18} />
                      Add First Pincode
                    </Button>
                  )}
                </div>
              ) : (
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>S.No</th>
                      <th>Pincode</th>
                      <th>Societies</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredPincodes.map((p, index) => (
                      <tr key={p.pincode}>
                        <td>{index + 1}</td>
                        <td>
                          <div className="pincode-name-cell">
                            <MapPin size={18} className="pincode-icon" />
                            <span className="pincode-badge">{p.pincode}</span>
                          </div>
                        </td>
                        <td>
                          <span className="society-count-badge">{p.societyCount} societies</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}

          {/* Societies Tab */}
          {activeTab === 'societies' && (
            <div className="table-container">
              {filteredSocieties.length === 0 ? (
                <div className="empty-state">
                  <Buildings size={48} className="empty-state-icon" />
                  <p>{searchTerm ? 'No societies found' : 'No societies added yet'}</p>
                  {!searchTerm && (
                    <Button variant="primary" onClick={handleAddSociety}>
                      <Plus size={18} />
                      Add First Society
                    </Button>
                  )}
                </div>
              ) : (
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Society Name</th>
                      <th>Pincode</th>
                      <th>Additional Address</th>
                      <th className="text-center">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredSocieties.map((society) => (
                      <tr key={society._id}>
                        <td>
                          <div className="society-name-cell">
                            <Buildings size={18} className="society-icon" />
                            <span>{society.name}</span>
                          </div>
                        </td>
                        <td>
                          <span className="pincode-badge">{society.pincode}</span>
                        </td>
                        <td>
                          <span className="text-muted">
                            {society.additionalAddress || '-'}
                          </span>
                        </td>
                        <td>
                          <div className="actions-cell">
                            <button
                              className="action-btn"
                              onClick={() => handleEditSociety(society)}
                              title="Edit"
                            >
                              <PencilSimple size={16} />
                            </button>
                            <button
                              className="action-btn danger"
                              onClick={() => handleDeleteClick(society)}
                              title="Delete"
                            >
                              <Trash size={16} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Add Pincode Modal */}
      <Modal
        isOpen={showPincodeModal}
        onClose={() => setShowPincodeModal(false)}
        title="Add New Pincode"
      >
        <div className="modal-form">
          {formErrors.submit && (
            <div className="form-error-banner">
              <Warning size={18} />
              {formErrors.submit}
            </div>
          )}

          <div className="form-group">
            <label className="form-label">Pincode *</label>
            <input
              type="text"
              className={`form-input ${formErrors.pincode ? 'error' : ''}`}
              value={pincodeForm.pincode}
              onChange={(e) => setPincodeForm({ pincode: e.target.value })}
              placeholder="Enter 6-digit pincode"
              maxLength={6}
            />
            {formErrors.pincode && <span className="form-error">{formErrors.pincode}</span>}
            <span className="form-hint">A default society will be created for this pincode</span>
          </div>

          <div className="modal-actions">
            <Button variant="outline" onClick={() => setShowPincodeModal(false)}>
              Cancel
            </Button>
            <Button variant="primary" onClick={handleSavePincode} disabled={saving}>
              {saving ? 'Adding...' : 'Add Pincode'}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Add/Edit Society Modal */}
      <Modal
        isOpen={showSocietyModal}
        onClose={() => setShowSocietyModal(false)}
        title={editingSociety ? 'Edit Society' : 'Add New Society'}
      >
        <div className="modal-form">
          {formErrors.submit && (
            <div className="form-error-banner">
              <Warning size={18} />
              {formErrors.submit}
            </div>
          )}

          <div className="form-group">
            <label className="form-label">Society Name *</label>
            <input
              type="text"
              className={`form-input ${formErrors.name ? 'error' : ''}`}
              value={societyForm.name}
              onChange={(e) => setSocietyForm({ ...societyForm, name: e.target.value })}
              placeholder="Enter society name"
            />
            {formErrors.name && <span className="form-error">{formErrors.name}</span>}
          </div>

          <div className="form-group">
            <label className="form-label">Pincode *</label>
            {uniquePincodes.length > 0 ? (
              <select
                className={`form-select ${formErrors.pincode ? 'error' : ''}`}
                value={societyForm.pincode}
                onChange={(e) => setSocietyForm({ ...societyForm, pincode: e.target.value })}
              >
                <option value="">Select Pincode</option>
                {uniquePincodes.map(pin => (
                  <option key={pin} value={pin}>{pin}</option>
                ))}
              </select>
            ) : (
              <div className="no-pincodes-message">
                <Warning size={16} />
                <span>No pincodes available. Please add a pincode first.</span>
              </div>
            )}
            {formErrors.pincode && <span className="form-error">{formErrors.pincode}</span>}
          </div>

          <div className="form-group">
            <label className="form-label">Additional Address</label>
            <input
              type="text"
              className="form-input"
              value={societyForm.additionalAddress}
              onChange={(e) => setSocietyForm({ ...societyForm, additionalAddress: e.target.value })}
              placeholder="e.g., Sector 15, Near Metro Station"
            />
          </div>

          <div className="modal-actions">
            <Button variant="outline" onClick={() => setShowSocietyModal(false)}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleSaveSociety}
              disabled={saving || uniquePincodes.length === 0}
            >
              {saving ? 'Saving...' : (editingSociety ? 'Update' : 'Add Society')}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        title="Delete Society"
      >
        <div className="delete-confirmation">
          <Warning size={48} className="warning-icon" />
          <p>Are you sure you want to delete <strong>{deletingSociety?.name}</strong>?</p>
          <p className="warning-text">
            This action cannot be undone. Customers in this society may be affected.
          </p>
          <div className="modal-actions">
            <Button variant="outline" onClick={() => setShowDeleteModal(false)}>
              Cancel
            </Button>
            <Button variant="danger" onClick={handleDeleteSociety} disabled={saving}>
              {saving ? 'Deleting...' : 'Delete'}
            </Button>
          </div>
        </div>
      </Modal>
      </div>
    </>
  );
}

export default Locations;
